// Input schemas for the answer widgets, shared by the Worker (the tool
// definitions the model fills in) and the browser (the guard that drops old
// or invalid tool input instead of rendering a broken card).
//
// Display widgets render the tool input. Input widgets (ask_clarification,
// pick_scope) end the run; the user's choice comes back as the next question.
// The email draft and scope picker save the user's edits per conversation (widget_state).

import { z } from 'zod'

const slug = z.string().min(1).max(63).describe('System slug from the scope, e.g. "p21-cloud"')
const text = (max) => z.string().min(1).max(max)
const httpMethod = z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'])
const authSecret = z
  .string()
  .max(60)
  .optional()
  .describe('Name of the bearer-token secret as the skill writes it, e.g. "p21_token". Default "access_token"')

// ---- show_endpoint: request builder (REST and OData) ------------------------

export const endpointSchema = z.object({
  system: slug,
  api: z.enum(['rest', 'odata']).default('rest').describe('"odata" for a table or view query'),
  variant: z.enum(['primary', 'secondary']).default('primary'),
  method: httpMethod,
  path: text(300).describe(
    'Exactly as in the skill, with {Placeholders}, e.g. {base}/api/entity/customers/{CompanyId}_{CustomerId}/. For odata: the table URL, e.g. {base}/odataservice/odata/table/oe_hdr',
  ),
  op_id: text(80),
  summary: z.string().max(120).optional().describe('Secondary rows: e.g. "Kit items go here instead"'),
  auth_secret: authSecret,
  path_params: z
    .array(z.object({ name: text(60), example: z.string().max(80).optional() }))
    .max(8)
    .optional()
    .describe('Each {Placeholder} in the path (not {base}) with a realistic example value'),
  query: z
    .array(
      z.object({
        name: text(60),
        required: z.boolean().default(false),
        example: z.string().max(120).optional(),
      }),
    )
    .max(16)
    .optional(),
  expand_options: z
    .array(text(60))
    .max(12)
    .optional()
    .describe('Related records the call can include, e.g. ["ShipTos", "Contacts"]'),
  expand_param: z.string().max(30).optional().describe('Query key for includes. Default "$expand"'),
  body: z.string().max(3000).optional().describe('Request body example, verbatim from the skill'),
  body_note: z
    .object({ field: z.string().max(60), text: z.string().max(120) })
    .optional()
    .describe('e.g. { field: "UnitQuantity", text: "= the break quantity" }'),
  response_fields: z.array(z.string().max(60)).max(16).optional(),
  notes: z
    .array(text(200))
    .max(6)
    .optional()
    .describe('Rules the request already follows, from the skill, e.g. "The key is {CompanyId}_{CustomerId}; the bare id returns 404"'),
  // OData only
  table: z.string().max(80).optional().describe('odata: table or view name'),
  company_column: z.string().max(60).optional().describe('odata: the company column on this table'),
  key_column: z.string().max(60).optional().describe('odata: the id column, for "only these ids"'),
  columns: z.array(text(60)).max(30).optional().describe('odata: selectable columns, verbatim'),
  default_columns: z.array(text(60)).max(30).optional(),
  date_column: z.string().max(60).optional().describe('odata: the last-modified column for "changed since"'),
  odata_version: z.enum(['v3', 'v4']).optional().describe('Picks the date literal format'),
  quote_filter: z.string().max(120).optional().describe("odata: filter that excludes quotes, e.g. projected_order eq 'N'"),
  max_or_ids: z.number().int().min(1).max(200).optional().describe('odata: most ids per or-chain. Default 50'),
})

/** Older answers used required_query / optional_query; still readable. */
export const endpointReadSchema = endpointSchema.extend({
  required_query: z.array(z.string().max(60)).max(12).optional(),
  optional_query: z.array(z.string().max(60)).max(12).optional(),
})

// ---- show_fields: mapping grid / field table ---------------------------------

export const fieldsSchema = z.object({
  title: z.string().max(80).optional().describe('e.g. "Customer fields" or "Sales order header mapping"'),
  source_system: slug,
  target_system: slug.optional().describe('Only for a mapping between two systems'),
  fields: z
    .array(
      z.object({
        name: text(80).describe('Field name verbatim from the skill'),
        target: z.string().max(80).optional().describe('Target field, verbatim, when mapping to another system'),
        type: z.string().max(30).optional().describe('e.g. string, decimal, date, bool'),
        required: z.boolean().optional(),
        purpose: z.string().max(200).optional().describe('Field questions only: what the field holds. Leave out for mappings'),
        note: z.string().max(160).optional().describe('Transform, default, lookup or gotcha'),
      }),
    )
    .min(1)
    .max(40),
})

// ---- show_gotchas -------------------------------------------------------------

export const GOTCHA_SEVERITIES = ['blocker', 'caution', 'tip']
export const GOTCHA_CATEGORIES = ['data', 'api', 'client', 'process', 'decision']

export const gotchasSchema = z.object({
  title: z.string().max(80).optional().describe('Default "Watch for"'),
  subject: z.string().max(80).optional().describe('e.g. "Inventory pipe"'),
  items: z
    .array(
      z.object({
        id: text(40),
        severity: z.enum(GOTCHA_SEVERITIES),
        category: z.enum(GOTCHA_CATEGORIES),
        title: text(120).describe('The trap in plain words'),
        why: text(400),
        fix: text(400).describe('What to do instead'),
        example: z.string().max(600).optional().describe('A short request or value that shows the fix'),
        attach_to: z
          .object({
            kind: z.enum(['endpoint', 'field', 'step']),
            ref: text(100).describe('endpoint: its op_id · field: the field name · step: the step op_id'),
          })
          .optional()
          .describe('Show it on that card instead of in the list'),
      }),
    )
    .min(1)
    .max(12),
})

// ---- show_pipe_flow -----------------------------------------------------------

const flowStep = z.object({
  system: slug,
  label: text(60).describe('Short action, e.g. "Find changed customers"'),
  op_id: text(80),
  method: z.string().max(10).optional(),
  path: z.string().max(300).optional(),
  note: z.string().max(300).optional(),
  watch: z.string().max(300).optional(),
})

export const pipeFlowSchema = z.object({
  pipe: text(60).describe('e.g. "Customer / Contact"'),
  complexity: z.string().max(40).optional(),
  flows: z
    .array(
      z.object({
        direction_label: text(60).describe('e.g. "P21 → storefront"'),
        steps: z.array(flowStep).min(1).max(8),
      }),
    )
    .min(1)
    .max(2),
})

// ---- show_handoff -------------------------------------------------------------

export const handoffSchema = z.object({
  source_system: slug,
  target_system: slug,
  title: z.string().max(80).optional().describe('e.g. "Sales order handoff"'),
  steps: z
    .array(
      z.object({
        system: slug,
        label: text(80),
        op_id: text(100),
        kind: z.enum(['request', 'payload', 'note']).default('request').describe('payload: an incoming webhook or message'),
        method: httpMethod.optional(),
        path: z.string().max(400).optional().describe('Verbatim, with any query string'),
        body: z.string().max(3000).optional(),
        auth_secret: authSecret,
        checks: z.array(text(240)).max(6).default([]).describe('What to check in the response before moving on'),
        keep: z.array(text(120)).max(6).default([]).describe('Ids to keep for later steps, e.g. "order_no = 1048213"'),
        watch: z.string().max(300).optional(),
      }),
    )
    .min(1)
    .max(10),
  travelling_ids: z
    .array(z.object({ from: text(100), to: text(100) }))
    .max(6)
    .default([])
    .describe('Ids that cross systems, e.g. { from: "AXIM order id", to: "P21 web_reference_no" }'),
})

// ---- pick_scope (input) -------------------------------------------------------

export const scopeSchema = z.object({
  systems: z.array(slug).max(4).default([]),
  pipes: z
    .array(
      z.object({
        key: text(40),
        name: text(60),
        direction: text(40).describe('e.g. "P21 → eCom", "eCom → P21", "Both"'),
        complexity: z.string().max(40).optional(),
        default_on: z.boolean().default(true),
      }),
    )
    .min(1)
    .max(14),
})

// ---- show_questionnaire -----------------------------------------------------

export const questionnaireSchema = z.object({
  title: z.string().max(80).optional(),
  groups: z
    .array(
      z.object({
        name: text(40),
        questions: z
          .array(
            z.object({
              id: text(40).describe("From the skill's question bank"),
              text: text(300),
            }),
          )
          .min(1)
          .max(15),
      }),
    )
    .min(1)
    .max(8),
})

// ---- show_checklist -----------------------------------------------------------

export const checklistSchema = z.object({
  title: text(80).describe('e.g. "Day-0 readiness"'),
  subject: z.string().max(80).optional().describe('e.g. "Acme Supply · P21 Cloud"'),
  items: z
    .array(
      z.object({
        id: text(40),
        item: text(160),
        owner: z.string().max(80).optional(),
        ready_when: z.string().max(200).optional(),
      }),
    )
    .min(1)
    .max(20),
})

// ---- show_email_draft (stateful) ----------------------------------------------

export const EMAIL_PURPOSES = {
  samples: 'Sample data request',
  credentials: 'Credentials request',
  questions: 'Discovery questions',
  decisions: 'Decisions follow-up',
  status: 'Day-0 status update',
  custom: 'Email',
}

export const emailDraftSchema = z.object({
  purpose: z.enum(Object.keys(EMAIL_PURPOSES)),
  subject: text(160),
  client_name: z.string().max(60).optional(),
  recipient_name: z.string().max(60).optional(),
  body: text(4000).describe('The email body without greeting or sign-off'),
  warnings: z.array(text(200)).max(4).optional().describe('Checks before sending, e.g. "Re-check the IP list"'),
})

// ---- ask_clarification (input) ------------------------------------------------

export const clarifySchema = z.object({
  question: text(200),
  options: z
    .array(z.object({ label: text(80), description: z.string().max(120).optional() }))
    .min(2)
    .max(4),
  allow_free_text: z.boolean().default(true),
})

// ---- Registry -----------------------------------------------------------------

/** Tool name → schema the browser validates finished input against. */
export const WIDGET_SCHEMAS = {
  show_endpoint: endpointReadSchema,
  show_fields: fieldsSchema,
  show_gotchas: gotchasSchema,
  show_pipe_flow: pipeFlowSchema,
  show_handoff: handoffSchema,
  show_questionnaire: questionnaireSchema,
  show_checklist: checklistSchema,
  show_email_draft: emailDraftSchema,
  ask_clarification: clarifySchema,
  pick_scope: scopeSchema,
}

/** Tools that wait for the user: the run ends after them. */
export const INPUT_TOOLS = ['ask_clarification', 'pick_scope']

/** Tools from older answers whose schemas are gone; never rendered. */
export const LEGACY_TOOLS = ['show_flow', 'show_rules', 'show_decisions', 'show_watch_for', 'cite_sources']
